import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import currencyJs from 'currency.js';
import { IAchPushDisbursementData } from 'types/order/escrow/IAchPushDisbursementData';
import { deleteAchPushAction, deleteAchPushSlice } from 'store/order/escrow/disbursements/achPushes/deleteAchPushSlice';
import { sendAchPushAction } from 'store/order/escrow/disbursements/achPushes/sendAchPushSlice';
import { useAppDispatch } from '../../../../store/store';

interface UseCreateEditAchPushProps {
  escrowFileId: string;
  availableAmountDollars: number;
  onTransactionUpdate: () => void;
}

export const useCreateEditAchPush = ({
  escrowFileId,
  availableAmountDollars,
  onTransactionUpdate,
}: UseCreateEditAchPushProps) => {
  const dispatch = useAppDispatch();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showDeletingACHPush, setShowDeletingACHPush] = useState(false);
  const [sendAchId, setSendAchId] = useState<string | undefined>(undefined);

  const {
    sendAchPush: { fetchStatus, fetchError },
    deleteAchPush: { fetchStatus: deleteAchPushStatus },
  } = useSelector((store: RootStore) => store.order.escrow.disbursements);

  useEffect(() => {
    if (deleteAchPushStatus.success && !showDeletingACHPush) {
      onTransactionUpdate();
      dispatch(deleteAchPushSlice.actions.reset({}));
    }
  }, [deleteAchPushStatus.success, showDeletingACHPush, onTransactionUpdate, dispatch]);

  const onRemoveAchPush = useCallback(
    async (achPushToRemove: IAchPushDisbursementData) => {
      if (achPushToRemove.id) {
        dispatch(deleteAchPushAction(achPushToRemove.id));
      } else {
        dispatch(deleteAchPushSlice.actions.setData({}));
      }
    },
    [dispatch]
  );

  const sendAchDisbursement = useCallback(
    async (achPush: IAchPushDisbursementData) => {
      setSendAchId(achPush.id);
      setShowWarningModal(false);
      await dispatch(
        sendAchPushAction({
          escrowFileId,
          achPushData: achPush,
        })
      );
      setSendAchId(undefined);
      onTransactionUpdate();
    },
    [dispatch, escrowFileId, onTransactionUpdate]
  );

  const onSendAchDisbursement = useCallback(
    async (achPush: IAchPushDisbursementData) => {
      if (achPush.amountCents > currencyJs(availableAmountDollars).multiply(100).value) {
        return setShowWarningModal(true);
      } else {
        await sendAchDisbursement(achPush);
      }
    },
    [availableAmountDollars, sendAchDisbursement]
  );

  return {
    showWarningModal,
    setShowWarningModal,
    showDeletingACHPush,
    setShowDeletingACHPush,
    sendAchId,
    fetchStatus,
    fetchError,
    onRemoveAchPush,
    onSendAchDisbursement,
  };
};
